import React, { useState } from 'react'
import { useAlumni } from '../../contexts/AlumniContext'
import FileUpload from './FileUpload'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Star, 
  User, 
  Briefcase, 
  Mail, 
  Phone, 
  MapPin,
  Eye,
  EyeOff,
  Award,
  BarChart,
  ChevronDown,
  ChevronUp,
  Globe,
  Calendar,
  Filter,
  X
} from 'lucide-react'

const AlumniManagement = () => {
  const {
    successStories,
    testimonials,
    statistics,
    registrations,
    storySubmissions,
    addSuccessStory,
    updateSuccessStory,
    deleteSuccessStory,
    approveStorySubmission,
    addTestimonial,
    updateTestimonial,
    deleteTestimonial,
    addStatistic,
    updateStatistic,
    deleteStatistic,
    updateStorySubmission,
    deleteStorySubmission,
    deleteRegistration,
    loading
  } = useAlumni()

  const [activeTab, setActiveTab] = useState('success-stories')
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [editingItem, setEditingItem] = useState(null)
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [selectedItems, setSelectedItems] = useState([])

  // Form state
  const [formData, setFormData] = useState({})

  // Stats for dashboard
  const stats = {
    totalSuccessStories: successStories.length,
    activeSuccessStories: successStories.filter(s => s.isActive).length,
    featuredSuccessStories: successStories.filter(s => s.featured).length,
    totalTestimonials: testimonials.length,
    activeTestimonials: testimonials.filter(t => t.isActive).length,
    featuredTestimonials: testimonials.filter(t => t.featured).length,
    totalStatistics: statistics.length,
    pendingRegistrations: registrations.filter(r => r.status === 'pending').length,
    pendingSubmissions: storySubmissions.filter(s => s.status === 'pending').length
  }

  // Modal handlers
  const openModal = (type, item = null) => {
    setModalType(type)
    setEditingItem(item)
    setIsModalOpen(true)
    
    if (item) {
      setFormData(item)
    } else {
      // Default form data based on type
      if (type === 'success-story') {
        setFormData({
          name: '',
          batchYear: '',
          title: '',
          company: '',
          story: '',
          image: '',
          achievements: [''],
          email: '',
          phone: '',
          linkedin: '',
          address: '',
          graduationYear: '',
          currentPosition: '',
          isActive: true,
          featured: false
        })
      } else if (type === 'testimonial') {
        setFormData({
          name: '',
          batchYear: '',
          testimonial: '',
          rating: 5,
          email: '',
          phone: '',
          profession: '',
          company: '',
          graduationYear: '',
          isActive: true,
          featured: false
        })
      } else if (type === 'statistic') {
        setFormData({
          icon: 'Users',
          number: '',
          label: '',
          order: statistics.length + 1,
          isActive: true
        })
      }
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setModalType('')
    setEditingItem(null)
    setFormData({})
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (modalType === 'success-story') {
      if (editingItem) {
        updateSuccessStory(editingItem._id, formData)
      } else {
        addSuccessStory(formData)
      }
    } else if (modalType === 'testimonial') {
      if (editingItem) {
        updateTestimonial(editingItem._id, formData)
      } else {
        addTestimonial(formData)
      }
    } else if (modalType === 'statistic') {
      if (editingItem) {
        updateStatistic(editingItem._id, formData)
      } else {
        addStatistic(formData)
      }
    }
    
    closeModal()
  }

  const handleDelete = (type, id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      if (type === 'success-story') {
        deleteSuccessStory(id)
      } else if (type === 'testimonial') {
        deleteTestimonial(id)
      } else if (type === 'statistic') {
        deleteStatistic(id)
      }
    }
  }

  const handleBulkAction = (action) => {
    if (selectedItems.length === 0) return
    
    if (action === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selectedItems.length} items?`)) {
        selectedItems.forEach(id => {
          if (activeTab === 'success-stories') {
            deleteSuccessStory(id)
          } else if (activeTab === 'testimonials') {
            deleteTestimonial(id)
          } else if (activeTab === 'statistics') {
            deleteStatistic(id)
          }
        })
        setSelectedItems([])
      }
    } else if (action === 'activate') {
      selectedItems.forEach(id => {
        if (activeTab === 'success-stories') {
          updateSuccessStory(id, { isActive: true })
        } else if (activeTab === 'testimonials') {
          updateTestimonial(id, { isActive: true })
        } else if (activeTab === 'statistics') {
          updateStatistic(id, { isActive: true })
        }
      })
      setSelectedItems([])
    } else if (action === 'deactivate') {
      selectedItems.forEach(id => {
        if (activeTab === 'success-stories') {
          updateSuccessStory(id, { isActive: false })
        } else if (activeTab === 'testimonials') {
          updateTestimonial(id, { isActive: false })
        } else if (activeTab === 'statistics') {
          updateStatistic(id, { isActive: false })
        }
      })
      setSelectedItems([])
    }
  }

  // Filter and sort data
  const filterAndSortData = (data) => {
    let filtered = data.filter(item => {
      const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.testimonial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           item.label?.toLowerCase().includes(searchTerm.toLowerCase())
      
      return matchesSearch
    })

    return filtered.sort((a, b) => {
      let aValue = a[sortBy] || ''
      let bValue = b[sortBy] || ''
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })
  }

  const getCurrentData = () => {
    switch (activeTab) {
      case 'success-stories':
        return filterAndSortData(successStories)
      case 'testimonials':
        return filterAndSortData(testimonials)
      case 'statistics':
        return filterAndSortData(statistics)
      default:
        return []
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-purple"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Alumni Network Management</h1>
          <p className="text-gray-600">Manage success stories, testimonials, and alumni statistics</p>
        </div>

        {/* Dashboard Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-primary-purple">
            <div className="flex items-center">
              <Star className="h-8 w-8 text-primary-purple" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Success Stories</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.activeSuccessStories}/{stats.totalSuccessStories}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-primary-magenta">
            <div className="flex items-center">
              <User className="h-8 w-8 text-primary-magenta" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Testimonials</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.activeTestimonials}/{stats.totalTestimonials}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-primary-yellow">
            <div className="flex items-center">
              <BarChart className="h-8 w-8 text-primary-yellow" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Statistics</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalStatistics}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-primary-green">
            <div className="flex items-center">
              <Mail className="h-8 w-8 text-primary-green" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Registrations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingRegistrations}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-primary-blue">
            <div className="flex items-center">
              <Award className="h-8 w-8 text-primary-blue" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Story Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingSubmissions}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'success-stories', label: 'Success Stories', icon: Star },
                { id: 'testimonials', label: 'Testimonials', icon: User },
                { id: 'statistics', label: 'Statistics', icon: BarChart },
                { id: 'registrations', label: 'Registrations', icon: Mail },
                { id: 'submissions', label: 'Submissions', icon: Award }
              ].map(tab => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-primary-purple text-primary-purple'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Toolbar */}
            {(activeTab === 'success-stories' || activeTab === 'testimonials' || activeTab === 'statistics') && (
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {selectedItems.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">{selectedItems.length} selected</span>
                      <button
                        onClick={() => handleBulkAction('activate')}
                        className="px-3 py-1 bg-green-100 text-green-700 rounded-md text-sm hover:bg-green-200"
                      >
                        Activate
                      </button>
                      <button
                        onClick={() => handleBulkAction('deactivate')}
                        className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-md text-sm hover:bg-yellow-200"
                      >
                        Deactivate
                      </button>
                      <button
                        onClick={() => handleBulkAction('delete')}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      if (activeTab === 'success-stories') openModal('success-story')
                      else if (activeTab === 'testimonials') openModal('testimonial')
                      else if (activeTab === 'statistics') openModal('statistic')
                    }}
                    className="bg-primary-purple text-white px-4 py-2 rounded-lg hover:bg-primary-purple/90 flex items-center space-x-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add New</span>
                  </button>
                </div>
              </div>
            )}

            {/* Content based on active tab */}
            {activeTab === 'success-stories' && (
              <SuccessStoriesTable 
                data={getCurrentData()}
                onEdit={(item) => openModal('success-story', item)}
                onDelete={(id) => handleDelete('success-story', id)}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                updateSuccessStory={updateSuccessStory}
              />
            )}

            {activeTab === 'testimonials' && (
              <TestimonialsTable 
                data={getCurrentData()}
                onEdit={(item) => openModal('testimonial', item)}
                onDelete={(id) => handleDelete('testimonial', id)}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                updateTestimonial={updateTestimonial}
              />
            )}

            {activeTab === 'statistics' && (
              <StatisticsTable 
                data={getCurrentData()}
                onEdit={(item) => openModal('statistic', item)}
                onDelete={(id) => handleDelete('statistic', id)}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                updateStatistic={updateStatistic}
              />
            )}

            {activeTab === 'registrations' && (
              <RegistrationsTable 
                data={registrations}
                deleteRegistration={deleteRegistration}
              />
            )}

            {activeTab === 'submissions' && (
              <SubmissionsTable 
                data={storySubmissions}
                approveStorySubmission={approveStorySubmission}
                updateStorySubmission={updateStorySubmission}
                deleteStorySubmission={deleteStorySubmission}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <Modal
          type={modalType}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={closeModal}
          isEditing={!!editingItem}
        />
      )}
    </div>
  )
}

// Success Stories Table Component
const SuccessStoriesTable = ({ data, onEdit, onDelete, selectedItems, onSelectionChange, updateSuccessStory }) => {
  const toggleSelection = (id) => {
    if (selectedItems.includes(id)) {
      onSelectionChange(selectedItems.filter(item => item !== id))
    } else {
      onSelectionChange([...selectedItems, id])
    }
  }

  const toggleSelectAll = () => {
    if (selectedItems.length === data.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(data.map(item => item._id))
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedItems.length === data.length && data.length > 0}
                onChange={toggleSelectAll}
                className="rounded text-primary-purple focus:ring-primary-purple"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Alumni
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Profession
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((story) => (
            <tr key={story._id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(story._id)}
                  onChange={() => toggleSelection(story._id)}
                  className="rounded text-primary-purple focus:ring-primary-purple"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <img 
                    className="h-10 w-10 rounded-full object-cover" 
                    src={story.image?.url || '/logo.jpeg'} 
                    alt={story.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/logo.jpeg';
                    }}
                  />
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{story.name}</div>
                    <div className="text-sm text-gray-500">{story.batchYear}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{story.title}</div>
                <div className="text-sm text-gray-500">{story.company}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateSuccessStory(story._id, { isActive: !story.isActive })}
                    className={`p-1 rounded ${story.isActive ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                  >
                    {story.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => updateSuccessStory(story._id, { featured: !story.featured })}
                    className={`p-1 rounded ${story.featured ? 'text-yellow-600 hover:bg-yellow-100' : 'text-gray-400 hover:bg-gray-100'}`}
                  >
                    <Star className={`h-4 w-4 ${story.featured ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(story)}
                    className="text-primary-purple hover:text-primary-purple/80"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(story._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Testimonials Table Component
const TestimonialsTable = ({ data, onEdit, onDelete, selectedItems, onSelectionChange, updateTestimonial }) => {
  const toggleSelection = (id) => {
    if (selectedItems.includes(id)) {
      onSelectionChange(selectedItems.filter(item => item !== id))
    } else {
      onSelectionChange([...selectedItems, id])
    }
  }

  const toggleSelectAll = () => {
    if (selectedItems.length === data.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(data.map(item => item._id))
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedItems.length === data.length && data.length > 0}
                onChange={toggleSelectAll}
                className="rounded text-primary-purple focus:ring-primary-purple"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Alumni
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Testimonial
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rating
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((testimonial) => (
            <tr key={testimonial._id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(testimonial._id)}
                  onChange={() => toggleSelection(testimonial._id)}
                  className="rounded text-primary-purple focus:ring-primary-purple"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-500">{testimonial.batchYear}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate">{testimonial.testimonial}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updateTestimonial(testimonial._id, { isActive: !testimonial.isActive })}
                    className={`p-1 rounded ${testimonial.isActive ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                  >
                    {testimonial.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => updateTestimonial(testimonial._id, { featured: !testimonial.featured })}
                    className={`p-1 rounded ${testimonial.featured ? 'text-yellow-600 hover:bg-yellow-100' : 'text-gray-400 hover:bg-gray-100'}`}
                  >
                    <Star className={`h-4 w-4 ${testimonial.featured ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(testimonial)}
                    className="text-primary-purple hover:text-primary-purple/80"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(testimonial._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Statistics Table Component
const StatisticsTable = ({ data, onEdit, onDelete, selectedItems, onSelectionChange, updateStatistic }) => {
  const toggleSelection = (id) => {
    if (selectedItems.includes(id)) {
      onSelectionChange(selectedItems.filter(item => item !== id))
    } else {
      onSelectionChange([...selectedItems, id])
    }
  }

  const toggleSelectAll = () => {
    if (selectedItems.length === data.length) {
      onSelectionChange([])
    } else {
      onSelectionChange(data.map(item => item._id))
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedItems.length === data.length && data.length > 0}
                onChange={toggleSelectAll}
                className="rounded text-primary-purple focus:ring-primary-purple"
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Statistic
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Value
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Order
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((stat) => (
            <tr key={stat._id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(stat._id)}
                  onChange={() => toggleSelection(stat._id)}
                  className="rounded text-primary-purple focus:ring-primary-purple"
                />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{stat.label}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 font-bold">{stat.number}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                  {stat.order}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => updateStatistic(stat._id, { isActive: !stat.isActive })}
                  className={`p-1 rounded ${stat.isActive ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-100'}`}
                >
                  {stat.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(stat)}
                    className="text-primary-purple hover:text-primary-purple/80"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(stat._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Registrations Table Component - With Delete Action
const RegistrationsTable = ({ data, deleteRegistration }) => {
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this registration?')) {
      try {
        await deleteRegistration(id)
        alert('Registration deleted successfully!')
      } catch (error) {
        alert('Failed to delete registration')
      }
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Contact
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Batch Year
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Current Profession
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Company
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Submit Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((registration) => (
            <tr key={registration._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{registration.name}</div>
                {registration.address && (
                  <div className="text-sm text-gray-500 max-w-xs truncate" title={registration.address}>
                    {registration.address}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{registration.email}</div>
                <div className="text-sm text-gray-500">{registration.phone}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{registration.batchYear}</div>
                {registration.graduationYear && (
                  <div className="text-sm text-gray-500">Grad: {registration.graduationYear}</div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {registration.currentProfession || '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {registration.company || '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {registration.createdAt ? new Date(registration.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  onClick={() => handleDelete(registration._id)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete registration"
                >
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No registrations found
        </div>
      )}
    </div>
  )
}

// Submissions Table Component
const SubmissionsTable = ({ data, approveStorySubmission, updateStorySubmission, deleteStorySubmission }) => {
  const handleApprove = async (id) => {
    if (window.confirm('Approve this story and make it public?')) {
      try {
        await approveStorySubmission(id)
        alert('Story approved successfully!')
      } catch (error) {
        alert('Failed to approve story')
      }
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this story submission?')) {
      try {
        await deleteStorySubmission(id)
      } catch (error) {
        alert('Failed to delete story submission')
      }
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Title
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Story Preview
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Submit Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((submission) => (
            <tr key={submission._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{submission.name}</div>
                <div className="text-sm text-gray-500">{submission.batchYear}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{submission.title}</div>
                {submission.company && (
                  <div className="text-sm text-gray-500">{submission.company}</div>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 max-w-xs truncate" title={submission.story}>
                  {submission.story}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {submission.createdAt ? new Date(submission.createdAt).toLocaleDateString() : 'N/A'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <button
                  onClick={() => handleApprove(submission._id)}
                  className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700"
                  title="Approve and publish story"
                >
                  <Award className="h-4 w-4 mr-1" />
                  Approve
                </button>
                <button
                  onClick={() => handleDelete(submission._id)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete story"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No story submissions found
        </div>
      )}
    </div>
  )
}

// Modal Component
const Modal = ({ type, formData, setFormData, onSubmit, onClose, isEditing }) => {
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const addAchievement = () => {
    const achievements = formData.achievements || ['']
    setFormData(prev => ({ ...prev, achievements: [...achievements, ''] }))
  }

  const updateAchievement = (index, value) => {
    const achievements = [...(formData.achievements || [])]
    achievements[index] = value
    setFormData(prev => ({ ...prev, achievements }))
  }

  const removeAchievement = (index) => {
    const achievements = formData.achievements.filter((_, i) => i !== index)
    setFormData(prev => ({ ...prev, achievements }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit' : 'Add'} {type === 'success-story' ? 'Success Story' : type === 'testimonial' ? 'Testimonial' : 'Statistic'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          {type === 'success-story' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
                  <input
                    type="text"
                    value={formData.batchYear || ''}
                    onChange={(e) => updateFormData('batchYear', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    placeholder="Class of 2010"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Profession</label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={(e) => updateFormData('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company || ''}
                    onChange={(e) => updateFormData('company', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Success Story</label>
                <textarea
                  value={formData.story || ''}
                  onChange={(e) => updateFormData('story', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Achievements</label>
                {(formData.achievements || ['']).map((achievement, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={achievement}
                      onChange={(e) => updateAchievement(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                      placeholder="Achievement"
                    />
                    {formData.achievements?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAchievement(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addAchievement}
                  className="text-primary-purple hover:text-primary-purple/80 text-sm flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Achievement</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => updateFormData('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
                <input
                  type="text"
                  value={formData.graduationYear || ''}
                  onChange={(e) => updateFormData('graduationYear', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <FileUpload
                  onFileSelect={(dataUrl, file) => {
                    // Store the File object for upload, not the data URL
                    updateFormData('image', file || dataUrl)
                  }}
                  currentImageUrl={typeof formData.image === 'string' ? formData.image : formData.image?.url}
                  accept={{ 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] }}
                  maxSize={5 * 1024 * 1024} // 5MB
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive || false}
                    onChange={(e) => updateFormData('isActive', e.target.checked)}
                    className="rounded text-primary-purple focus:ring-primary-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured || false}
                    onChange={(e) => updateFormData('featured', e.target.checked)}
                    className="rounded text-primary-purple focus:ring-primary-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Featured</span>
                </label>
              </div>
            </>
          )}

          {type === 'testimonial' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Batch Year</label>
                  <input
                    type="text"
                    value={formData.batchYear || ''}
                    onChange={(e) => updateFormData('batchYear', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    placeholder="Class of 2010"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Testimonial</label>
                <textarea
                  value={formData.testimonial || ''}
                  onChange={(e) => updateFormData('testimonial', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <select
                  value={formData.rating || 5}
                  onChange={(e) => updateFormData('rating', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                >
                  <option value={5}>5 Stars</option>
                  <option value={4}>4 Stars</option>
                  <option value={3}>3 Stars</option>
                  <option value={2}>2 Stars</option>
                  <option value={1}>1 Star</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive || false}
                    onChange={(e) => updateFormData('isActive', e.target.checked)}
                    className="rounded text-primary-purple focus:ring-primary-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.featured || false}
                    onChange={(e) => updateFormData('featured', e.target.checked)}
                    className="rounded text-primary-purple focus:ring-primary-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Featured</span>
                </label>
              </div>
            </>
          )}

          {type === 'statistic' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <select
                    value={formData.icon || 'Users'}
                    onChange={(e) => updateFormData('icon', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  >
                    <option value="Users">Users</option>
                    <option value="Briefcase">Briefcase</option>
                    <option value="Award">Award</option>
                    <option value="Network">Network</option>
                    <option value="Globe">Globe</option>
                    <option value="Star">Star</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                  <input
                    type="number"
                    value={formData.order || 1}
                    onChange={(e) => updateFormData('order', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                    min="1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number/Value</label>
                <input
                  type="text"
                  value={formData.number || ''}
                  onChange={(e) => updateFormData('number', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  placeholder="10000+ or 85%"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={formData.label || ''}
                  onChange={(e) => updateFormData('label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-purple/20 focus:border-primary-purple"
                  placeholder="Alumni Worldwide"
                  required
                />
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive || false}
                    onChange={(e) => updateFormData('isActive', e.target.checked)}
                    className="rounded text-primary-purple focus:ring-primary-purple"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-purple text-white rounded-lg hover:bg-primary-purple/90"
            >
              {isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AlumniManagement